import React from 'react';
import { AiAnalysisData } from '../shared/types';
import './AiSessionSummary.scss';

interface AiSessionSummaryProps {
  data: AiAnalysisData;
  isRegenerating: boolean;
  regenerationError: string | null;
  onRegenerate: () => void;
}

const AiSessionSummary: React.FC<AiSessionSummaryProps> = ({
  data,
  isRegenerating,
  regenerationError,
  onRegenerate,
}) => {
  const tags = data.gpt_tags ?? [];
  const hasStatus = data.gpt_resolved !== undefined || data.gpt_escalated === true;

  return (
    <section className="ca-ai-summary" aria-labelledby="ca-ai-summary-title">
      <div className="ca-ai-summary__header">
        <h2 className="ca-ai-summary__title" id="ca-ai-summary-title">
          <span className="material-icons" aria-hidden="true">
            auto_awesome
          </span>
          AI SESSION SUMMARY
        </h2>
        <button
          className="ca-ai-summary__regenerate"
          type="button"
          onClick={onRegenerate}
          disabled={isRegenerating}
        >
          <span className="material-icons" aria-hidden="true">
            refresh
          </span>
          {isRegenerating ? 'Regenerating...' : 'Regenerate'}
        </button>
      </div>

      <p className="ca-ai-summary__text">{data.gpt_summary ?? 'No AI summary available.'}</p>

      {hasStatus && (
        <div className="ca-ai-summary__statuses" aria-label="Session status">
          {data.gpt_resolved !== undefined && (
            <span
              className={`ca-ai-summary__status ca-ai-summary__status--${
                data.gpt_resolved ? 'resolved' : 'unresolved'
              }`}
            >
              <span className="material-icons" aria-hidden="true">
                {data.gpt_resolved ? 'check_circle' : 'cancel'}
              </span>
              {data.gpt_resolved ? 'Resolved' : 'Unresolved'}
            </span>
          )}
          {data.gpt_escalated && (
            <span className="ca-ai-summary__status ca-ai-summary__status--escalated">
              <span aria-hidden="true">!</span>
              Escalated
            </span>
          )}
        </div>
      )}

      {tags.length > 0 && (
        <div className="ca-ai-summary__topics">
          <span className="ca-ai-summary__section-label">Tags</span>
          <div className="ca-ai-summary__tags">
            {tags.map((tag, index) => (
              <span key={`${tag}-${index}`} className="ca-ai-summary__tag">
                {tag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="ca-ai-summary__transfers">
        <span className="material-icons" aria-hidden="true">
          swap_horiz
        </span>
        <span>
          <strong>{data.gpt_transfers_bot_agent ?? 0}</strong> bot→agent
          <span className="ca-ai-summary__transfer-separator">·</span>
          <strong>{data.gpt_transfers_agent_agent ?? 0}</strong> agent→agent
        </span>
      </div>

      {regenerationError && (
        <div className="ca-ai-summary__error" role="alert">
          <span className="material-icons" aria-hidden="true">
            error_outline
          </span>
          {regenerationError}
        </div>
      )}
    </section>
  );
};

export default AiSessionSummary;
